/**
 * Rating & Review UI components.
 *
 * - StarRating      — display-only star row (e.g. on profile cards)
 * - ReviewForm      — interactive star picker + comment textarea
 * - ReviewList      — list of reviews for a target (job/user/store)
 * - ReviewSection   — composite: form (if eligible) + list
 *
 * Schema: prisma Review model (jobId, reviewerId, targetId, targetType,
 * rating 1-5, comment). Creating a review atomically updates the
 * denormalized rating + ratingCount on User/Store.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LoadingState, EmptyState } from "@/components/features/states";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatRelativeTime } from "@/lib/format";

// ------------------------------------------------------------------
// StarRating — display-only.
// ------------------------------------------------------------------

export function StarRating({
  value,
  size = 16,
  count,
  showCount = false,
}: {
  value: number;
  size?: number;
  count?: number;
  showCount?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            style={{ width: size, height: size }}
            className={
              n <= Math.round(value)
                ? "fill-yellow-400 text-yellow-400"
                : "fill-muted text-muted-foreground"
            }
          />
        ))}
      </div>
      <span className="text-sm font-medium">{value.toFixed(1)}</span>
      {showCount && count !== undefined && (
        <span className="text-xs text-muted-foreground">({count})</span>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// StarPicker — interactive star input for the review form.
// ------------------------------------------------------------------

function StarPicker({
  value,
  onChange,
  size = 32,
}: {
  value: number;
  onChange: (v: number) => void;
  size?: number;
}) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          <Star
            style={{ width: size, height: size }}
            className={
              n <= (hover || value)
                ? "fill-yellow-400 text-yellow-400"
                : "fill-muted text-muted-foreground"
            }
          />
        </button>
      ))}
      {value > 0 && (
        <span className="ml-2 text-sm font-medium text-muted-foreground">
          {value}/5
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// ReviewForm — submit a review for a job target.
// ------------------------------------------------------------------

export function ReviewForm({
  jobId,
  targetType,
  targetLabel,
  onSubmitted,
}: {
  jobId: string;
  targetType: "courier" | "customer" | "store";
  targetLabel: string;
  onSubmitted?: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      api.reviews.create({ jobId, targetType, rating, comment: comment.trim() || undefined }),
    onSuccess: () => {
      toast.success(t("review.submitted") || "Review submitted — thank you!");
      qc.invalidateQueries({ queryKey: ["reviews", jobId] });
      setRating(0);
      setComment("");
      onSubmitted?.();
    },
    onError: (e: any) => {
      toast.error(e?.error || t("review.submitFailed") || "Failed to submit review");
    },
  });

  const handleSubmit = () => {
    if (rating === 0) {
      toast.error(t("review.selectRating") || "Please select a rating");
      return;
    }
    mutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("review.rate") || "Rate"} {targetLabel}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <StarPicker value={rating} onChange={setRating} />
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("review.commentPlaceholder") || "Share your experience (optional)..."}
          rows={3}
          maxLength={500}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{comment.length}/500</span>
          <Button onClick={handleSubmit} disabled={mutation.isPending || rating === 0}>
            <Send className="mr-2 h-4 w-4" />
            {mutation.isPending
              ? t("common.submitting") || "Submitting..."
              : t("review.submit") || "Submit review"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------
// ReviewList — list reviews for a target.
// ------------------------------------------------------------------

export function ReviewList({
  jobId,
  targetId,
  targetType,
}: {
  jobId?: string;
  targetId?: string;
  targetType?: string;
}) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["reviews", jobId, targetId, targetType],
    queryFn: () =>
      api.reviews.list({
        jobId: jobId,
        targetId: targetId,
        targetType: targetType,
      }),
    enabled: !!jobId || !!targetId,
  });

  const reviews = data?.data?.reviews ?? [];

  if (isLoading) return <LoadingState />;
  if (reviews.length === 0) {
    return (
      <EmptyState
        icon={Star}
        title={t("review.noReviews") || "No reviews yet"}
        description={t("review.noReviewsDesc") || "Be the first to leave a review."}
      />
    );
  }

  return (
    <div className="space-y-3">
      {reviews.map((review: any) => (
        <Card key={review.id}>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback>
                  {review.reviewer?.name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {review.reviewer?.name || t("review.anonymous") || "Anonymous"}
                    </span>
                    <Badge variant="outline" className="text-xs capitalize">
                      {review.targetType}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatRelativeTime(review.createdAt)}
                  </span>
                </div>
                <StarRating value={review.rating} size={14} />
                {review.comment && (
                  <p className="mt-2 text-sm text-muted-foreground">{review.comment}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// ReviewSection — composite used on the job-detail page.
// Shows the review form (if the user is eligible) + existing reviews.
// ------------------------------------------------------------------

export function ReviewSection({
  jobId,
  targetType,
  targetLabel,
  canReview,
}: {
  jobId: string;
  targetType: "courier" | "customer" | "store";
  targetLabel: string;
  canReview: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Star className="h-5 w-5 text-yellow-400" />
        <h3 className="text-lg font-semibold">
          {t("review.reviews") || "Reviews"}
        </h3>
      </div>
      {canReview && (
        <ReviewForm jobId={jobId} targetType={targetType} targetLabel={targetLabel} />
      )}
      <ReviewList jobId={jobId} />
    </div>
  );
}
